/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ K2 — SEEDS / ONBOARDING · welcome seed runner                           │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * AGNOSTIC runner for the app: receives creation mutations via injection
 * (`WelcomeSeedDeps`) and a declarative spec (`WelcomeSeedSpec`). Each clone
 * propagates kit by injecting ITS mutations and domain spec; here only
 * orchestration lives (list first, tasks second, collected ids).
 *
 * Texts arrive ALREADY TRANSLATED from UI layer (lingui lives in app, not
 * in kit): seed is created in active user language.
 */

import { nowISO } from "../utils/date-helpers";

/** Sample seed task (flags -> listto domain columns). */
export interface WelcomeSeedTodoSpec {
  text: string;
  /** Mark important (appears in Important view). */
  important?: boolean;
  /** Due date = now (appears in Planned view). */
  dueToday?: boolean;
  /** Add to My Day today. */
  myDay?: boolean;
  /** Attached note (teaches task detail). */
  note?: string;
}

export interface WelcomeSeedSpec {
  listName: string;
  todos: WelcomeSeedTodoSpec[];
}

/** Translated texts that UI resolves with lingui and passes to builder. */
export interface WelcomeSeedStrings {
  listName: string;
  completeTask: string;
  starImportant: string;
  dueDatePlanned: string;
  myDayPlan: string;
  openDetails: string;
  detailsNote: string;
}

/**
 * Canonical listto spec: a "Getting started" list with 5 tasks
 * teaching completion, Important, Planned, My Day and task detail.
 */
export function buildWelcomeSeedSpec(strings: WelcomeSeedStrings): WelcomeSeedSpec {
  return {
    listName: strings.listName,
    todos: [
      { text: strings.completeTask },
      { text: strings.starImportant, important: true },
      { text: strings.dueDatePlanned, dueToday: true },
      { text: strings.myDayPlan, myDay: true },
      { text: strings.openDetails, note: strings.detailsNote },
    ],
  };
}

/**
 * Injected mutations: signatures compatible with `createListMutation` /
 * `createTodoMutation` of canon (clones inject their own).
 */
export interface WelcomeSeedDeps<TDb> {
  createList(db: TDb, userId: string, name: string): Promise<string>;
  createTodo(
    db: TDb,
    userId: string,
    text: string,
    opts?: {
      listId?: string;
      important?: boolean;
      dueDate?: string;
      myDayDate?: string;
      note?: string;
    },
  ): Promise<string>;
}

export interface WelcomeSeedResult {
  listId: string;
  todoIds: string[];
}

/**
 * Runs seed: creates list and then its tasks IN ORDER (spec order
 * is pedagogical order seen by user). Does not swallow errors: if creation
 * fails, caller decides (onboarding card maintains state
 * "undecided" and user can retry).
 */
export async function applyWelcomeSeed<TDb>(
  db: TDb,
  userId: string,
  spec: WelcomeSeedSpec,
  deps: WelcomeSeedDeps<TDb>,
  opts?: { now?: () => string },
): Promise<WelcomeSeedResult> {
  const now = opts?.now ?? nowISO;
  const listId = await deps.createList(db, userId, spec.listName);
  const todoIds: string[] = [];
  for (const todoSpec of spec.todos) {
    const todoId = await deps.createTodo(db, userId, todoSpec.text, {
      listId,
      important: todoSpec.important ?? false,
      dueDate: todoSpec.dueToday ? now() : undefined,
      myDayDate: todoSpec.myDay ? now() : undefined,
      note: todoSpec.note,
    });
    todoIds.push(todoId);
  }
  return { listId, todoIds };
}
